/**
 * `PlanComparison` - the customer-facing plan picker: cards ("Everything in X, plus:")
 * + the full feature comparison table + Upgrade/Downgrade buttons relative to the current
 * plan. SHARED between web_admin (PlanPage) and web_account (BillingSection) - locale +
 * callbacks come in as props, with inline et/en i18n (like the rest of `plans/`).
 *
 * The cards do NOT repeat every feature of every plan: the first plan shows its base, the
 * ones after it show only what they ADD (the delta over the previous plan). The full matrix
 * is the expandable table below.
 *
 * Which rows either view shows is decided in `comparison-rows.ts` (React-free).
 */
import { useEffect, useMemo, useRef, useState, type Ref } from "react";
import { Check, X, Sparkles, Infinity as InfinityIcon, ChevronDown, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "../components/button";
import { Badge } from "../components/badge";
import { ScrollFade } from "../components/scroll-fade";
import { makeT, featureLabel, featureShortLabel, featureValueText, widgetTierLabel, localeText, formatMoney } from "./i18n";
import { orderedCategories, planDelta, rollUpWidgetTiers } from "./comparison-rows";
import { PlanChangeDialog, addOnTargets, defaultResolution, type QuotaResolutionState } from "./change-dialog";
import {
  UNLIMITED,
  type AddOnTargetInput,
  type EntitlementCatalogEntry,
  type EntitlementMap,
  type EntitlementValue,
  type Locale,
  type LocaleMap,
  type PlanChangePreview,
} from "./types";

export type PlanChangeKind = "current" | "upgrade" | "downgrade";

/**
 * Preview-server config errors (`POST /api/billing/preview`): the payment gateway is not
 * ready for this plan/cycle. These CANNOT be overridden with "Continue anyway" - the real
 * switch (`/checkout`) would hit the same gate. The most common one: `plan_not_synced` (e.g.
 * the yearly price is unsynced while monthly is fine) → a cycle switch would otherwise show
 * the confusing "couldn't calculate". Every other error (a Stripe glitch etc.) is treated as
 * transient (`calc`).
 */
const CONFIG_ERROR_CODES = new Set([
  "plan_not_synced",
  "unknown_plan",
  "stripe_not_configured",
  "host_site_unmanaged",
]);

/** Read the server error code from a thrown `ApiError` (`.body.error`) - client-agnostically. */
function errorCode(err: unknown): string {
  if (err && typeof err === "object") {
    const body = (err as { body?: unknown }).body;
    if (body && typeof body === "object" && "error" in body) {
      return String((body as { error: unknown }).error);
    }
  }
  return "";
}

export interface PlanComparisonPlan {
  code: string;
  name: LocaleMap;
  description?: LocaleMap | null;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  currency: string;
  features: EntitlementMap;
  featured?: boolean;
  badge?: LocaleMap | null;
  sortOrder: number;
  /** Free trial (days); 0 = off. */
  trialDays?: number;
}

export interface PlanComparisonProps {
  plans: PlanComparisonPlan[];
  catalog: EntitlementCatalogEntry[];
  /** Code of the current plan - the "Current" badge plus the basis for upgrade/downgrade. */
  currentCode: string;
  /** Billing cycle of the current subscription - lets the customer switch their own plan's
   *  cycle (monthly↔yearly) in place (selected `cycle` ≠ `currentCycle`). Defaults to "monthly". */
  currentCycle?: "monthly" | "yearly";
  locale: Locale;
  cycle: "monthly" | "yearly";
  onCycleChange: (c: "monthly" | "yearly") => void;
  /**
   * The user CONFIRMED a plan (after the confirm dialog when `onPreview` is given; otherwise
   * straight on click). `kind` = upgrade/downgrade relative to the current plan. May be async -
   * the dialog waits for it and shows the "Confirm" button loading meanwhile.
   *
   * `addOns` = absolute add-on target quantities the user picked in the dialog (e.g. "+1
   * site", so a shrinking site quota still covers every site). They MUST ride in the same
   * switch call, otherwise the customer gets two prorations and two invoices.
   */
  onChoose: (
    planCode: string,
    kind: "upgrade" | "downgrade",
    opts?: { addOns?: AddOnTargetInput[] },
  ) => void | Promise<void>;
  /**
   * Optional proration preview. When given, a click FIRST opens the confirm dialog (with the
   * Stripe-calculated amount) and `onChoose` runs only on "Confirm". Absent → the old
   * behaviour (straight to `onChoose`). `change:"new"` (no live subscription) skips the
   * dialog → straight to `onChoose` (Checkout confirms).
   */
  onPreview?: (
    planCode: string,
    billingCycle: "monthly" | "yearly",
    addOns?: AddOnTargetInput[],
  ) => Promise<PlanChangePreview>;
  /**
   * Persist the surviving sites (`POST /api/account/sites/keep`). Given -> on a shrinking
   * quota the dialog also offers the "choose which sites stay" path. Absent (e.g. the user
   * is not the account owner) -> only the extra-site purchase remains.
   *
   * Called AFTER the plan switch: if the switch fails, every site stays live rather than
   * the other way round.
   */
  onKeepSites?: (siteIds: string[]) => Promise<void>;
  /** The plan currently being chosen (planCode) - its button shows loading. */
  busyCode?: string | null;
  /** Every button blocked (e.g. the payment gateway is not configured). */
  disabled?: boolean;
  /** The account already used its trial (1× guarantee) → do not offer the trial CTA. */
  trialUsed?: boolean;
  /** Highlight one plan (a ring) + scroll to it - a signed-in user arriving from the public
   *  signup funnel via `?plan=<code>` lands here. Absent → nothing (the normal view).
   *  An unknown code is ignored. */
  highlightCode?: string;
}

export function PlanComparison({
  plans,
  catalog,
  currentCode,
  currentCycle = "monthly",
  locale,
  cycle,
  onCycleChange,
  onChoose,
  onPreview,
  onKeepSites,
  busyCode,
  disabled,
  trialUsed,
  highlightCode,
}: PlanComparisonProps) {
  const t = makeT(locale);
  const [showTable, setShowTable] = useState(false);

  // Highlighted plan (from the funnel `?plan=`) → scroll to the START of the card (not its
  // middle), horizontally centered (the mobile scroller). The card's `scroll-mt-*`
  // (scroll-margin-top) accounts for the sticky header - `block:"start"` lands the card's top
  // edge below the header rather than behind it. Scrolling happens for EVERY selected plan
  // (the current one included). The ring shows ONLY when the plan differs from the current
  // one: the current plan already has its own border + "Current" badge, so a ring on top of
  // that would look duplicated or broken.
  const highlightRef = useRef<HTMLDivElement>(null);
  const ringHighlight = highlightCode && highlightCode !== currentCode ? highlightCode : undefined;
  useEffect(() => {
    if (highlightCode && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" });
    }
  }, [highlightCode]);

  // ── Confirm dialog (proration preview) ───────────────────────────────────────
  const [pending, setPending] = useState<{ planCode: string; kind: "upgrade" | "downgrade" } | null>(null);
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [previewState, setPreviewState] = useState<"loading" | "ready" | "error">("loading");
  // Error kind: "config" = the payment gateway is not ready for this cycle (e.g. an unsynced
  // yearly price) → "Continue anyway" would fail the same way, so hide it. "calc" = a
  // transient calculation error → allow continuing (the server enforces). See continueAnyway.
  const [previewErrorKind, setPreviewErrorKind] = useState<"calc" | "config">("calc");
  const [applying, setApplying] = useState(false);
  // How the user covers a shrinking site quota (buy the add-on vs pick sites). Lives here
  // because switching mode means a FRESH preview - the add-on really changes the amount and
  // adding it up client-side would drift from Stripe's own calculation.
  const [resolution, setResolution] = useState<QuotaResolutionState>({ mode: "addon", keepIds: [] });

  function closeDialog() {
    setPending(null);
    setPreview(null);
    setApplying(false);
  }

  /** Load the preview (optionally with add-on targets) and set the dialog state. */
  function loadPreview(planCode: string, kind: "upgrade" | "downgrade", addOns: AddOnTargetInput[], seedResolution: boolean) {
    if (!onPreview) return;
    setPreviewErrorKind("calc");
    setPreviewState("loading");
    onPreview(planCode, cycle, addOns.length > 0 ? addOns : undefined)
      .then((p) => {
        // No live subscription → the switch would go to Checkout; no dialog needed.
        if (p.change === "new") {
          closeDialog();
          void onChoose(planCode, kind);
          return;
        }
        // On the first load pick the default path (buy the add-on when available); on later
        // loads (the user switched mode) keep their choice.
        if (seedResolution) setResolution(defaultResolution(p.quotaOptions));
        setPreview(p);
        setPreviewState("ready");
      })
      .catch((err: unknown) => {
        // Config errors (the gateway has no price for this cycle / no such plan) arrive from
        // the server with a code - they are NOT transient and the real switch would fail
        // identically.
        const code = errorCode(err);
        setPreviewErrorKind(CONFIG_ERROR_CODES.has(code) ? "config" : "calc");
        setPreviewState("error");
      });
  }

  /** Card click: no preview → apply straight away; with a preview → open the dialog. */
  function handleChoose(planCode: string, kind: "upgrade" | "downgrade") {
    if (!onPreview) {
      void onChoose(planCode, kind); // old behaviour
      return;
    }
    setPending({ planCode, kind });
    setPreview(null);
    loadPreview(planCode, kind, [], true);
  }

  /** Quota choice changed: "buy extra sites" <-> "pick sites" -> fresh preview from Stripe. */
  function handleResolutionChange(next: QuotaResolutionState) {
    setResolution(next);
    if (!pending || next.mode === resolution.mode) return; // only a site checkbox moved
    loadPreview(pending.planCode, pending.kind, addOnTargets(next, preview?.quotaOptions), false);
  }

  /**
   * "Confirm" - the real switch happens ONLY here.
   *
   * Order: plan (+ add-ons, one call) BEFORE the site choice. The reverse order would
   * validate the choice against the OLD (higher) quota and suspend sites before the payment
   * succeeds; this way the worst case (keep fails) leaves the customer exactly where they
   * would have been anyway - the grace deadline plus the banner on the billing page.
   */
  async function confirmChange() {
    if (!pending) return;
    setApplying(true);
    try {
      await onChoose(pending.planCode, pending.kind, { addOns: addOnTargets(resolution, preview?.quotaOptions) });
      if (resolution.mode === "keep" && resolution.keepIds.length > 0 && onKeepSites) {
        await onKeepSites(resolution.keepIds);
      }
    } finally {
      closeDialog();
    }
  }

  /** The preview failed but the server enforces anyway → let the user continue. */
  function continueAnyway() {
    if (!pending) return;
    const { planCode, kind } = pending;
    closeDialog();
    void onChoose(planCode, kind);
  }

  const sorted = useMemo(() => [...plans].sort((a, b) => a.sortOrder - b.sortOrder), [plans]);
  const currentRank = useMemo(() => {
    const cur = sorted.find((p) => p.code === currentCode);
    return cur ? cur.sortOrder : -Infinity;
  }, [sorted, currentCode]);
  // The trial CTA is offered ONLY on a free→paid move (the server grants a trial only on a
  // new subscription). On a paid plan an "upgrade" is an in-place switch (no trial).
  // `trialUsed` covers the "already tried" case.
  const currentIsFree = useMemo(() => {
    const cur = sorted.find((p) => p.code === currentCode);
    return !cur || (cur.priceMonthlyCents <= 0 && cur.priceYearlyCents <= 0);
  }, [sorted, currentCode]);
  const trialsAvailable = currentIsFree && !trialUsed;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-center">
        <CycleToggle cycle={cycle} onCycleChange={onCycleChange} t={t} />
      </div>

      {/* Cards with horizontal scrolling: on a narrow screen they scroll and the edges fade
          out (ScrollFade); on a wide one they all fit in a row and grow to fill it (flex-1). */}
      <ScrollFade className="-mx-1 px-1 py-3" deps={[sorted.length, cycle]}>
        <div className="flex gap-4">
          {sorted.map((plan, i) => (
            <PlanCard
              key={plan.code}
              plan={plan}
              prev={sorted[i - 1] ?? null}
              catalog={catalog}
              locale={locale}
              cycle={cycle}
              currentCycle={currentCycle}
              kind={
                plan.code === currentCode
                  ? "current"
                  : plan.sortOrder > currentRank
                    ? "upgrade"
                    : "downgrade"
              }
              busy={busyCode === plan.code}
              disabled={Boolean(disabled)}
              trialAvailable={trialsAvailable}
              highlight={plan.code === ringHighlight}
              innerRef={plan.code === highlightCode ? highlightRef : undefined}
              onChoose={handleChoose}
              t={t}
            />
          ))}
        </div>
      </ScrollFade>

      <div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="mx-auto flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {showTable ? t("compare.hideTable") : t("compare.showTable")}
          <ChevronDown className={cn("size-4 transition-transform", showTable && "rotate-180")} />
        </button>
        {showTable && (
          <ComparisonTable plans={sorted} catalog={catalog} locale={locale} cycle={cycle} currentCode={currentCode} t={t} />
        )}
      </div>

      <PlanChangeDialog
        open={pending !== null}
        kind={pending?.kind ?? "upgrade"}
        state={previewState}
        errorKind={previewErrorKind}
        preview={preview}
        applying={applying}
        locale={locale}
        resolution={resolution}
        onResolutionChange={handleResolutionChange}
        allowKeep={Boolean(onKeepSites)}
        onConfirm={confirmChange}
        onContinue={continueAnyway}
        onClose={() => {
          if (!applying) closeDialog();
        }}
      />
    </div>
  );
}

function CycleToggle({
  cycle,
  onCycleChange,
  t,
}: {
  cycle: "monthly" | "yearly";
  onCycleChange: (c: "monthly" | "yearly") => void;
  t: (k: string) => string;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border p-0.5">
      {(["monthly", "yearly"] as const).map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onCycleChange(c)}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            cycle === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t(`cycle.${c}`)}
        </button>
      ))}
    </div>
  );
}

function PlanCard({
  plan,
  prev,
  catalog,
  locale,
  cycle,
  currentCycle,
  kind,
  busy,
  disabled,
  trialAvailable,
  highlight,
  innerRef,
  onChoose,
  t,
}: {
  plan: PlanComparisonPlan;
  prev: PlanComparisonPlan | null;
  catalog: EntitlementCatalogEntry[];
  locale: Locale;
  cycle: "monthly" | "yearly";
  currentCycle: "monthly" | "yearly";
  kind: PlanChangeKind;
  busy: boolean;
  disabled: boolean;
  trialAvailable: boolean;
  highlight?: boolean;
  innerRef?: Ref<HTMLDivElement>;
  onChoose: (planCode: string, kind: "upgrade" | "downgrade") => void;
  t: (k: string) => string;
}) {
  const name = localeText(plan.name, locale);
  const desc = plan.description ? localeText(plan.description, locale) : "";
  const isFree = plan.priceMonthlyCents <= 0 && plan.priceYearlyCents <= 0;
  const priceCents = cycle === "yearly" ? Math.round(plan.priceYearlyCents / 12) : plan.priceMonthlyCents;
  const isCurrent = kind === "current";
  // The current plan, but the selected cycle ≠ the running one → offer switching this plan's
  // billing cycle (monthly↔yearly) straight from the card. A free plan has no cycle.
  const cycleSwitch = isCurrent && !isFree && cycle !== currentCycle;
  // Trial CTA: a free→paid move (kind=upgrade), the plan has a trial and the account has not
  // used it. The action is the same `onChoose("upgrade")` - the server grants the trial.
  const trialDays = plan.trialDays ?? 0;
  const showTrial = kind === "upgrade" && !isFree && trialAvailable && trialDays > 0;
  const rows = useMemo(() => rollUpWidgetTiers(planDelta(plan, prev, catalog)), [plan, prev, catalog]);

  return (
    <div
      ref={innerRef}
      className={cn(
        // flex-1 + min-w → on a wide screen the card grows to fill the row, on a narrow one it
        // keeps a readable width and the ScrollFade container starts scrolling.
        // scroll-mt → when scrolled to from the funnel (`scrollIntoView block:"start"`) it
        // leaves room for the sticky header + the "Current" badge (which sits at -top-2.5).
        "relative flex min-w-[15rem] flex-1 flex-col scroll-mt-24 rounded-xl border p-5 transition-colors",
        isCurrent ? "border-primary bg-primary/5 shadow-soft" : "border-border bg-card/60",
        plan.featured && !isCurrent && "border-primary/40",
        // A plan highlighted from the funnel → a ring, so the eye finds the chosen plan.
        highlight && "ring-2 ring-primary ring-offset-2 ring-offset-background",
      )}
    >
      {/* The badge sits on the card's top edge (-top-2.5) → it must be OPAQUE and ABOVE the
          border line (z-10), otherwise the card's green border shows through it (which reads
          as a strikethrough). */}
      {isCurrent && (
        <Badge className="absolute -top-2.5 right-4 z-10 border-transparent bg-primary text-primary-foreground">
          {t("card.currentBadge")}
        </Badge>
      )}
      {!isCurrent && plan.featured && (
        <Badge variant="secondary" className="absolute -top-2.5 right-4 z-10">
          {plan.badge ? localeText(plan.badge, locale) : t("card.popular")}
        </Badge>
      )}

      <div className="flex items-center gap-2">
        {plan.code === "ai" && <Sparkles className="size-4 text-primary" />}
        <h3 className="text-base font-semibold">{name}</h3>
      </div>

      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-bold tracking-tight">
          {isFree ? t("card.free") : formatMoney(priceCents, plan.currency, locale)}
        </span>
        {!isFree && <span className="text-sm text-muted-foreground">/{t("card.perMonth")}</span>}
      </div>
      {cycle === "yearly" && !isFree && <p className="mt-0.5 text-xs text-muted-foreground">{t("card.billedYearly")}</p>}
      {desc && <p className="mt-2 text-sm text-muted-foreground">{desc}</p>}

      {/* "Everything in X, plus:" (or "Includes:" on the first one) + the delta list. */}
      <p className="mt-4 text-xs font-medium text-foreground">
        {prev ? (
          <>
            {t("compare.everythingPre")}
            <span className="font-semibold">{localeText(prev.name, locale)}</span>
            {t("compare.everythingPost")}
          </>
        ) : (
          t("compare.includes")
        )}
      </p>
      <ul className="mt-2 flex-1 space-y-1.5">
        {rows.map((row) => (
          <li key={row.key} className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-success" />
            <span className="text-muted-foreground">
              {row.tier ? (
                widgetTierLabel(row.tier, row.tierKeys!.length, locale)
              ) : row.def!.type === "boolean" ? (
                featureLabel(row.def!.key, locale)
              ) : (
                <>
                  {featureShortLabel(row.def!.key, locale)}:{" "}
                  <span className="font-medium text-foreground">
                    {featureValueText(row.def!.key, plan.features[row.def!.key], locale)}
                  </span>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-5">
        {cycleSwitch ? (
          <Button
            variant={cycle === "yearly" ? "primary" : "outline"}
            className="w-full"
            loading={busy}
            disabled={disabled}
            onClick={() => onChoose(plan.code, cycle === "yearly" ? "upgrade" : "downgrade")}
          >
            <RefreshCw className="size-4" /> {cycle === "yearly" ? t("card.switchToYearly") : t("card.switchToMonthly")}
          </Button>
        ) : isCurrent ? (
          <Button variant="outline" className="w-full" disabled>
            {t("card.currentBtn")}
          </Button>
        ) : isFree ? (
          <Button variant="outline" className="w-full" disabled>
            {t("card.includedFree")}
          </Button>
        ) : showTrial ? (
          <div className="space-y-1.5">
            <Button variant="primary" className="w-full" loading={busy} disabled={disabled} onClick={() => onChoose(plan.code, "upgrade")}>
              <Sparkles className="size-4" /> {t("card.trialCta")}
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">{trialDays} {t("card.trialHint")}</p>
          </div>
        ) : kind === "upgrade" ? (
          <Button variant="primary" className="w-full" loading={busy} disabled={disabled} onClick={() => onChoose(plan.code, "upgrade")}>
            <ArrowUp className="size-4" /> {t("card.upgradeBtn")}
          </Button>
        ) : (
          <Button variant="outline" className="w-full" loading={busy} disabled={disabled} onClick={() => onChoose(plan.code, "downgrade")}>
            <ArrowDown className="size-4" /> {t("card.downgradeBtn")}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The full feature matrix - rows = the catalog (by category), columns = plans. */
function ComparisonTable({
  plans,
  catalog,
  locale,
  cycle,
  currentCode,
  t,
}: {
  plans: PlanComparisonPlan[];
  catalog: EntitlementCatalogEntry[];
  locale: Locale;
  cycle: "monthly" | "yearly";
  currentCode: string;
  t: (k: string) => string;
}) {
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">{t("compare.featureCol")}</th>
            {plans.map((p) => {
              const isFree = p.priceMonthlyCents <= 0 && p.priceYearlyCents <= 0;
              const priceCents = cycle === "yearly" ? Math.round(p.priceYearlyCents / 12) : p.priceMonthlyCents;
              return (
                <th
                  key={p.code}
                  className={cn(
                    "px-4 py-3 text-center font-semibold",
                    p.code === currentCode && "bg-primary/5 text-primary",
                  )}
                >
                  <div>{localeText(p.name, locale)}</div>
                  <div className="text-xs font-normal text-muted-foreground">
                    {isFree ? t("card.free") : `${formatMoney(priceCents, p.currency, locale)}/${t("card.perMonth")}`}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {orderedCategories(catalog).map((category) => {
            const defs = catalog.filter((d) => d.category === category);
            if (defs.length === 0) return null;
            return (
              <FragmentCategory key={category} category={category} defs={defs} plans={plans} locale={locale} currentCode={currentCode} t={t} />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FragmentCategory({
  category,
  defs,
  plans,
  locale,
  currentCode,
  t,
}: {
  category: string;
  defs: EntitlementCatalogEntry[];
  plans: PlanComparisonPlan[];
  locale: Locale;
  currentCode: string;
  t: (k: string) => string;
}) {
  // Same roll-up as the cards: the fourteen exclusive widgets collapse into one row per tier.
  // Listing them individually would put fifteen rows under "Builder" and drown the block that
  // actually differentiates the plans (the widget ladder is one decision per tier, not per
  // widget - a customer picks "the flagship widgets", not "the cinema widget").
  const rows = rollUpWidgetTiers(defs);
  return (
    <>
      <tr className="border-b border-border bg-muted/20">
        <td colSpan={plans.length + 1} className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          {t(`cat.${category}`)}
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.key} className="border-b border-border/60 last:border-0">
          <td className="px-4 py-2.5 text-muted-foreground">
            {row.tier ? widgetTierLabel(row.tier, row.tierKeys!.length, locale) : featureLabel(row.def!.key, locale)}
          </td>
          {plans.map((p) => (
            <td key={p.code} className={cn("px-4 py-2.5 text-center", p.code === currentCode && "bg-primary/5")}>
              {row.tier ? (
                <TierCell keys={row.tierKeys!} features={p.features} />
              ) : (
                <TableCell def={row.def!} value={p.features[row.def!.key]} locale={locale} />
              )}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function TableCell({ def, value, locale }: { def: EntitlementCatalogEntry; value: EntitlementValue | undefined; locale: Locale }) {
  if (def.type === "boolean") {
    return value ? <Check className="mx-auto size-4 text-success" /> : <X className="mx-auto size-4 text-muted-foreground/30" />;
  }
  if (value === UNLIMITED) return <InfinityIcon className="mx-auto size-4 text-foreground" />;
  const n = typeof value === "number" ? value : 0;
  if (n === 0) return <X className="mx-auto size-4 text-muted-foreground/30" />;
  return <span className="font-medium tabular-nums text-foreground">{featureValueText(def.key, value, locale)}</span>;
}

/**
 * Cell of a rolled-up widget-tier row: whether this plan grants the whole tier.
 *
 * A partial tier ("3/6") is not decoration - the plan editor exposes every `widget.*` key
 * separately, so an admin can hand-edit a plan into a half-granted tier, and both a check and
 * a cross would be a lie about what the customer gets.
 */
function TierCell({ keys, features }: { keys: string[]; features: EntitlementMap }) {
  const granted = keys.reduce((n, key) => (features[key] === true ? n + 1 : n), 0);
  if (granted === keys.length) return <Check className="mx-auto size-4 text-success" />;
  if (granted === 0) return <X className="mx-auto size-4 text-muted-foreground/30" />;
  return <span className="font-medium tabular-nums text-foreground">{granted}/{keys.length}</span>;
}
